import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useRecoilState } from 'recoil';
import client from '../API/API';
import { logInStatus } from '../Store/store';
import AtomsLogo from '../assets/AtomsLogo.png';

// ── Inline icon ───────────────────────────────────────────────────────────────
const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const EyeOpen  = 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 12m-3 0a3 3 0 106 0 3 3 0 00-6 0';
const EyeClosed= 'M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24 M1 1l22 22';
const UserIcon = 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2 M12 11a4 4 0 100-8 4 4 0 000 8z';
const LockIcon = 'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2z M7 11V7a5 5 0 0110 0v4';

// ── Stats shown in the right panel ───────────────────────────────────────────
const STATS = [
  { label: 'Uptime', value: '99.2%' },
  { label: 'Telemetry lag', value: '< 2 s' },
  { label: 'Data points today', value: '1.2 M' },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const [, setIsLoggedIn] = useRecoilState(logInStatus);

  const [username, setUsername]   = useState('');
  const [password, setPassword]   = useState('');
  const [showPw,   setShowPw]     = useState(false);
  const [loading,  setLoading]    = useState(false);
  const [error,    setError]      = useState('');
  const [keepMe,   setKeepMe]     = useState(false);
  const [liveCount, setLiveCount] = useState(341);

  // Subtle live-count animation on right panel
  useEffect(() => {
    const id = setInterval(() => {
      setLiveCount(c => c + (Math.random() > 0.5 ? 1 : -1));
    }, 4000);
    return () => clearInterval(id);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Please enter your username and password.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await client.post('/api/login/', { username, password });
      const d   = res.data;
      if (d.access) {
        localStorage.setItem('token',       d.access);
        localStorage.setItem('refresh_token', d.refresh);
        localStorage.setItem('userid',      d.user_id);
        localStorage.setItem('username',    d.username || username);
        localStorage.setItem('pages',       JSON.stringify(d.pages || []));
        localStorage.setItem('role',        d.role        || 'user');
        localStorage.setItem('org_id',      d.org_id      || '');
        localStorage.setItem('org_name',    d.org_name    || 'ATOMS');
        localStorage.setItem('org_type',    d.org_type    || '');
        localStorage.setItem('org_logo_url',d.org_logo_url|| '');
        localStorage.setItem('org_color',   d.org_color   || '#0897BE');
        setIsLoggedIn(true);
        navigate('/app/dashboard');
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Invalid credentials.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      minHeight: '100vh',
      fontFamily: 'var(--font-sans)',
    }}>

      {/* ── Left: sign-in form ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '48px 8%',
        background: 'var(--bg-surface)',
      }}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          style={{ width: '100%', maxWidth: 400 }}
        >
          {/* Logo */}
          <img src={AtomsLogo} alt="ATOMS" style={{ height: 30, marginBottom: 32 }} />

          {/* Heading */}
          <h1 style={{
            fontFamily: 'var(--font-sans)', fontWeight: 600,
            fontSize: 28, lineHeight: 1.2, letterSpacing: '-0.6px',
            color: 'var(--ink-900)', marginBottom: 36,
          }}>
            Sign in
          </h1>

          {/* Error */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                background: 'var(--offline-bg)', color: 'var(--offline)',
                border: '1px solid var(--offline-border)',
                borderRadius: 'var(--r-md)', padding: '10px 14px',
                fontSize: 13, marginBottom: 20,
              }}
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Username */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ink-700)', marginBottom: 6 }}>
                Username
              </label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-300)', pointerEvents: 'none' }}>
                  <Icon d={UserIcon} size={16} />
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="Enter username"
                  autoComplete="username"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Password */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ink-700)', marginBottom: 6 }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-300)', pointerEvents: 'none' }}>
                  <Icon d={LockIcon} size={16} />
                </div>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter password"
                  autoComplete="current-password"
                  style={inputStyle}
                />
                <button type="button" onClick={() => setShowPw(v => !v)} style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-300)',
                  padding: 0, display: 'flex', alignItems: 'center',
                }}>
                  <Icon d={showPw ? EyeOpen : EyeClosed} size={16} />
                </button>
              </div>
            </div>

            {/* Keep me signed in */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--ink-500)' }}>
                <input
                  type="checkbox"
                  checked={keepMe}
                  onChange={e => setKeepMe(e.target.checked)}
                  style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                />
                Keep me signed in
              </label>
              <button type="button" style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 13, color: 'var(--accent-ink)', fontFamily: 'var(--font-sans)',
              }}>
                Forgot password?
              </button>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '13px 0',
                background: loading ? 'var(--border-strong)' : 'var(--accent)',
                color: '#fff', border: 'none',
                borderRadius: 'var(--r-md)',
                fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 14.5,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'background var(--t-fast)',
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background = 'var(--accent-hover)'; }}
              onMouseLeave={e => { if (!loading) e.currentTarget.style.background = 'var(--accent)'; }}
            >
              {loading ? <Spinner /> : 'Sign in'}
            </button>
          </form>

          {/* Footer */}
          <p style={{ marginTop: 40, fontSize: 10, color: 'var(--ink-300)', textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
            POWERED BY ATOMS · INDUSTRY 4.0
          </p>
        </motion.div>
      </div>

      {/* ── Right: brand panel ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        style={{
          background: 'linear-gradient(150deg, #0C0E16 0%, #14203a 60%, var(--accent-ink) 140%)',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          padding: '48px 8%',
          position: 'relative', overflow: 'hidden',
        }}
      >
        {/* Subtle dot-grid overlay */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.06,
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          pointerEvents: 'none',
        }} />

        {/* Live indicator */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 40, padding: '6px 14px',
          marginBottom: 32, alignSelf: 'flex-start',
        }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: 'var(--online)', display: 'inline-block',
            animation: 'atoms-pulse 2s infinite',
          }} />
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
            {liveCount} machines live right now
          </span>
        </div>

        <h2 style={{
          fontFamily: 'var(--font-sans)', fontWeight: 600,
          fontSize: 34, lineHeight: 1.2, letterSpacing: '-0.8px',
          color: '#fff', marginBottom: 20,
          textWrap: 'balance',
        }}>
          The intelligence layer for your industrial machines.
        </h2>

        <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.66)', lineHeight: 1.6, marginBottom: 40, maxWidth: 420 }}>
          Monitor, control, and reason about every machine in your fleet — from anywhere, in real time.
        </p>

        {/* Mono stats */}
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {STATS.map(s => (
            <div key={s.label}>
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 20, fontWeight: 500,
                color: '#fff', letterSpacing: '-0.5px',
              }}>
                {s.value}
              </div>
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 3 }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

// ── Shared input style ────────────────────────────────────────────────────────
const inputStyle = {
  width: '100%',
  padding: '10px 12px 10px 38px',
  fontFamily: 'var(--font-mono)',
  fontSize: 13,
  color: 'var(--ink-900)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--r-md)',
  outline: 'none',
  transition: 'border-color var(--t-fast), box-shadow var(--t-fast)',
  boxSizing: 'border-box',
};

// ── Mini spinner ──────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{
      width: 18, height: 18,
      border: '2px solid rgba(255,255,255,0.3)',
      borderTopColor: '#fff',
      borderRadius: '50%',
      animation: 'atoms-spin 0.7s linear infinite',
    }} />
  );
}
